import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import { Container, Reveal } from '../components/Reveal';
import { WhatsAppButton, BookButton } from '../components/CTAs';
import { breadcrumbSchema } from '../lib/schema';
import { track } from '../lib/track';
import { INTERNATIONAL } from '../data/international';

const MODES = { hub: 'hub', singapore: 'singapore', indonesia: 'indonesia' };

export default function InternationalPage() {
    const location = useLocation();
    const mode = location.pathname.includes('/singapore')
        ? 'singapore'
        : location.pathname.includes('indonesia-batam')
            ? 'indonesia'
            : 'hub';
    const { t, lang } = useLang();
    const key = MODES[mode] || 'hub';
    const c = INTERNATIONAL[key][lang] || INTERNATIONAL[key].en;
    const basePath = key === 'hub' ? '/international' : key === 'singapore' ? '/international/singapore' : '/international/indonesia-batam';

    return (
        <>
            <SEO
                title={c.metaTitle}
                description={c.metaDesc}
                path={basePath}
                lang={lang}
                jsonLd={[
                    breadcrumbSchema(lang, [
                        { name: t.nav.home, path: '/' },
                        { name: t.international.title, path: '/international' },
                        ...(key !== 'hub' ? [{ name: c.title, path: basePath }] : []),
                    ]),
                ]}
            />
            <section className="relative overflow-hidden bg-ink pt-28 pb-16 text-white md:pt-36 md:pb-20" data-testid="international-hero">
                <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-gold-400/20" aria-hidden="true" />
                <Container className="relative">
                    <Reveal>
                        <p className="font-serif text-lg italic text-peri-300">{c.kicker}</p>
                        <h1 className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-tight tracking-tight md:text-6xl">{c.title}</h1>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-peri-200">{c.heroText}</p>
                        {key !== 'hub' && (
                            <div className="mt-8 flex flex-wrap gap-3">
                                <WhatsAppButton label={t.cta.whatsappLong} source={`international-${key}`} className="!bg-wa hover:!bg-wa-hover" />
                                <BookButton label={t.cta.book} lang={lang} source={`international-${key}`} className="!border-peri-300 !text-peri-200 hover:!bg-white/10" />
                            </div>
                        )}
                    </Reveal>
                </Container>
            </section>

            {/* Mode switcher */}
            <div className="border-b border-peri-200 bg-white">
                <Container>
                    <div className="flex flex-wrap gap-2 py-4" data-testid="international-mode-tabs">
                        {[
                            { k: 'hub', to: '/international', label: t.international.title },
                            { k: 'singapore', to: '/international/singapore', label: 'Singapore' },
                            { k: 'indonesia', to: '/international/indonesia-batam', label: 'Indonesia / Batam' },
                        ].map((m) => (
                            <Link
                                key={m.k}
                                to={`/dental/${lang}${m.to}`}
                                data-testid={`international-tab-${m.k}`}
                                onClick={() => track('international_view', { market: m.k })}
                                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${m.k === key ? 'bg-gold-400 text-ink' : 'border border-peri-300 text-ink-muted hover:bg-peri-50'}`}
                            >
                                {m.label}
                            </Link>
                        ))}
                    </div>
                </Container>
            </div>

            {c.proposition && (
                <section className="bg-peri-50 py-16 md:py-20" data-testid="international-proposition">
                    <Container>
                        <Reveal>
                            <div className="rounded-3xl border border-peri-200 bg-white p-8 md:p-12">
                                <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{c.proposition.title}</h2>
                                <ul className="mt-8 grid gap-4 md:grid-cols-2">
                                    {c.proposition.points.map((p, i) => (
                                        <li key={i} className="flex items-start gap-3 rounded-2xl bg-peri-50 p-5 text-[15px] font-medium leading-relaxed text-ink-body">
                                            <span className="font-serif text-xl italic text-gold-700">{String(i + 1).padStart(2, '0')}</span>
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-6 rounded-2xl border border-dashed border-peri-400 p-5 text-xs italic leading-relaxed text-ink-subtle">{c.proposition.note}</p>
                            </div>
                        </Reveal>
                    </Container>
                </section>
            )}

            <section className="py-16 md:py-24" data-testid="international-sections">
                <Container>
                    <div className="grid gap-4 md:grid-cols-2">
                        {c.sections.map((s, i) => (
                            <Reveal key={s.h} delay={Math.min(i * 0.05, 0.3)} className="h-full">
                                <div className="h-full rounded-3xl border border-peri-200 bg-white p-7">
                                    <h2 className="font-serif text-xl font-semibold text-ink md:text-2xl">{s.h}</h2>
                                    <p className="mt-3 text-[15px] leading-relaxed text-ink-muted">{s.p}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            {key === 'hub' && (
                <section className="bg-peri-50 py-16 md:py-20">
                    <Container>
                        <div className="grid gap-4 md:grid-cols-2">
                            {[c.sgCard, c.idCard].map((card, i) => (
                                <Reveal key={card.title} delay={i * 0.08}>
                                    <Link
                                        to={`/dental/${lang}${card.to}`}
                                        data-testid={`hub-card-${i === 0 ? 'sg' : 'id'}`}
                                        onClick={() => track(i === 0 ? 'singapore_cta' : 'indonesia_cta', { source: 'hub' })}
                                        className="group block rounded-3xl border border-peri-200 bg-white p-8 transition-all hover:-translate-y-1 hover:border-gold-500 hover:shadow-card-hover"
                                    >
                                        <h3 className="font-serif text-2xl font-semibold text-ink">{card.title}</h3>
                                        <p className="mt-3 text-[15px] text-ink-muted">{card.text}</p>
                                        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold-700">
                                            {t.cta.explore}
                                            <span className="transition-transform duration-300 group-hover:translate-x-1">
                                                <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </span>
                                        </span>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            <section className="bg-ink py-14 text-white">
                <Container>
                    <Reveal className="mx-auto max-w-2xl text-center">
                        <p className="text-base leading-relaxed text-peri-200">{c.disclaimer || INTERNATIONAL.hub[lang]?.disclaimer || INTERNATIONAL.hub.en.disclaimer}</p>
                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <WhatsAppButton label={t.cta.whatsappLong} source={`international-${key}-bottom`} className="!bg-wa hover:!bg-wa-hover" />
                            <BookButton label={t.cta.book} lang={lang} source={`international-${key}-bottom`} className="!border-peri-300 !text-peri-200 hover:!bg-white/10" />
                        </div>
                    </Reveal>
                </Container>
            </section>
        </>
    );
}
